import type { MedicationData, MedicationOptionData } from "@qendo/medical/medication";
import type { PastSymptomData, PastSymptomOptionData } from "@qendo/medical/past_symptom";
import type { SurgeryData, SurgeryOptionData } from "@qendo/medical/surgery";
import { MedicalType } from "@qendo/medical/types";
import type { MedicalOptionType, OptionType } from "@qendo/types";
import type { Href } from "expo-router";
import type { Allergy } from "~/entities/medical/allergy";
import type { Condition } from "~/entities/medical/condition";
import type { Immunisation } from "~/entities/medical/immunisation";
import type { Medication } from "~/entities/medical/medication";
import type { PastSymptom } from "~/entities/medical/past_symptom";
import type { Supplement } from "~/entities/medical/supplement";
import type { Surgery } from "~/entities/medical/surgery";
import type { Trigger } from "~/entities/medical/trigger";
import type { AllergyData, AllergyOptionData } from "./allergy";
import type { ConditionData, ConditionOptionData } from "./condition";
import type { ImmunisationData, ImmunisationOptionData } from "./immunisation";
import type { SupplementData, SupplementOptionData } from "./supplement";
import type { TriggerData, TriggerOptionData } from "./trigger";

export type MedicalRecord<T extends MedicalType = MedicalType> = T extends MedicalType.allergy
    ? Allergy
    : T extends MedicalType.condition
      ? Condition
      : T extends MedicalType.immunisation
        ? Immunisation
        : T extends MedicalType.medication
          ? Medication
          : T extends MedicalType.past_symptom
            ? PastSymptom
            : T extends MedicalType.supplement
              ? Supplement
              : T extends MedicalType.surgery
                ? Surgery
                : T extends MedicalType.trigger
                  ? Trigger
                  : never;

export type MedicalRecordData<T extends MedicalType> = T extends MedicalType.allergy
    ? AllergyData
    : T extends MedicalType.condition
      ? ConditionData
      : T extends MedicalType.immunisation
        ? ImmunisationData
        : T extends MedicalType.medication
          ? MedicationData
          : T extends MedicalType.past_symptom
            ? PastSymptomData
            : T extends MedicalType.supplement
              ? SupplementData
              : T extends MedicalType.surgery
                ? SurgeryData
                : T extends MedicalType.trigger
                  ? TriggerData
                  : never;

export type StoredMedicalRecord<T extends MedicalType = MedicalType> = {
    id: string;
} & (MedicalRecordData<T> | MedicalRecord<T>);
export type NewMedicalRecord<T extends MedicalType = MedicalType> = {
    id?: string;
} & MedicalRecordData<T>;
export type ConcreteMedicalRecord<T extends MedicalType = MedicalType> = StoredMedicalRecord<T> & MedicalRecord<T>;

export type MedicalRecordPayload<
    T extends MedicalType = MedicalType,
    R = StoredMedicalRecord<T> | NewMedicalRecord<T>,
> = R extends { id: string } ? StoredMedicalRecord<T> : NewMedicalRecord<T>;

export type MedicalOptionData<T extends MedicalOptionType> = T extends OptionType.allergies
    ? AllergyOptionData
    : T extends OptionType.conditions
      ? ConditionOptionData
      : T extends OptionType.immunisations
        ? ImmunisationOptionData
        : T extends OptionType.medications
          ? MedicationOptionData
          : T extends OptionType.past_symptoms
            ? PastSymptomOptionData
            : T extends OptionType.supplements
              ? SupplementOptionData
              : T extends OptionType.surgeries
                ? SurgeryOptionData
                : T extends OptionType.triggers
                  ? TriggerOptionData
                  : never;
export const ALLERGY_BASE_URL = "/health-profile/allergies";
export const CONDITION_BASE_URL = "/health-profile/conditions";
export const IMMUNISATION_BASE_URL = "/health-profile/immunisations";
export const MEDICATION_BASE_URL = "/health-profile/medications";
export const SYMPTOM_BASE_URL = "/health-profile/past-symptoms";
export const SUPPLEMENT_BASE_URL = "/health-profile/supplements";
export const SURGERY_BASE_URL = "/health-profile/surgeries";
export const TRIGGER_BASE_URL = "/health-profile/triggers";

export const MedicalTypeBaseUrlMap: Record<MedicalType, Href> = {
    [MedicalType.allergy]: ALLERGY_BASE_URL,
    [MedicalType.condition]: CONDITION_BASE_URL,
    [MedicalType.immunisation]: IMMUNISATION_BASE_URL,
    [MedicalType.medication]: MEDICATION_BASE_URL,
    [MedicalType.past_symptom]: SYMPTOM_BASE_URL,
    [MedicalType.supplement]: SUPPLEMENT_BASE_URL,
    [MedicalType.surgery]: SURGERY_BASE_URL,
    [MedicalType.trigger]: TRIGGER_BASE_URL,
};
